import JournalEvent from "core/models/journal-event";
import dayjs from "dayjs";

/**
 * This utils aims to aggregate different JEs type by their occurredAt property, under a specific key.
 * For example, if we provide two different JEs with different occurredAt :
 * - Two PrArticulationsCountedJournalEvents, one the 2021-01-01, and another the 2021-01-05.
 * - Two PrFeelingEvaPatientDeclaredJournalEvent, one the 2021-01-01, and another the 2021-01-02
 * and an object which maps jes types to keys :
 * - {
 *  "pr-articulations-counted-journal-events": "nadNagJe",
 *  "pr-feeling-eva-patient-declared-journal-event": "patientFeelingJe"
 * }
 * It will return an array of object, each array representing a day, and the associated occurredAt :
 * [
 *  {
 *   nadNagJe: the PrArticulationsCountedJournalEvents which occurredAt is 2021-01-05
 *   occurredAt: the occurredAt of the JE, i.e. 2021-01-05
 *  },
 *  {
 *   patientFeelingJe: the PrFeelingEvaPatientDeclaredJournalEvent which occurredAt is 2021-01-02
 *   occurredAt: the occurredAt of the JE, i.e. 2021-01-02
 *  },
 *  {
 *    nadNagJe: the PrArticulationsCountedJournalEvents which occurredAt is 2021-01-01,
 *    patientFeelingJe: the PrFeelingEvaPatientDeclaredJournalEvent which occurredAt is 2021-01-01
 *    occurredAt: the occurredAt of the two JEs, i.e. 2021-01-01
 *  }
 * ]
 *
 * They are ordered in a desc. way given their occurredAt aggregate.
 *
 * @param {Array<JournalEvent>} jes All the JEs, no matter their type
 * @param {Object.<string, string>} jeTypeToKeyObject An object whichs maps a JE type to a specific key
 *
 * @returns {Array<{ occurredAt: Date} & Object.<string, JournalEvent>>} each item is grouped by occurredAt value
 */

export default function aggregateJesByOccurredAt(jes, jeTypeToKeyObject) {
  const currentReducedObject = jes.reduce((acc, cur) => {
    const shortDate = dayjs(cur.occurredAt).startOf("day").toString();
    const key = jeTypeToKeyObject[cur.summary.type];
    if (!acc.hasOwnProperty(shortDate)) {
      acc[shortDate] = {};
      acc[shortDate][key] = cur;
    } else {
      if (!acc[shortDate].hasOwnProperty(key) || dayjs(acc[shortDate][key].occurredAt).isBefore(cur.occurredAt)) {
        acc[shortDate][key] = cur;
      }
    }
    return acc;
  }, {});
  const orderedJesByOccurredAt = Object.entries(currentReducedObject).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
  return orderedJesByOccurredAt.map(item => Object.assign({
    occurredAt: item[0]
  }, item[1]));
}